<template>
  <div class="facilitator results">
    <section class="sales">
      <header>
        <h2>{{ $t('sales') }}</h2>
      </header>

      <dl class="panels">
        <div>
          <dt>{{ $t('requests') }}</dt>
          <dd>
            <span class="value">{{ sales.projectStats.requests }}</span>
          </dd>
        </div>
        <div>
          <dt>{{ $t('lab consulting applied') }}</dt>
          <dd>
            <span class="value">
              {{ sales.projectStats.consultingApplied }}
              <small>{{ $t('of which effective') }}: {{ sales.projectStats.consultingIncluded }}</small>
            </span>
          </dd>
        </div>
      </dl>
    </section>

    <section class="clients-nps">
      <header>
        <h2>{{ $t('Net promoter score') }}</h2>
      </header>

      <ul>
        <ClientNps v-for="client in clientsWithNps" :key="client.sqid" :client="client" />
      </ul>
    </section>

    <section class="technical">
      <header>
        <h2>{{ $t('technical') }}</h2>
      </header>

      <dl class="panels">
        <div>
          <dt>{{ $t('maintenance') }}</dt>
          <dd>
            <span class="value">
              {{ technical.maintenances }}
              <small>{{ $t('with coffee') }}: {{ technical.extraServices }}</small>
            </span>
          </dd>
        </div>
        <div>
          <dt>{{ $t('uptime') }}</dt>
          <dd>
            <span class="value">
              <NumberDisplay :value="technical.uptime" suffix="%" :digits="2" />
            </span>
          </dd>
        </div>
        <div>
          <dt>{{ $t('uptime bonus') }}</dt>
          <dd>
            <span class="value">
              <MoneyDisplay :value="technical.uptimeBonus" />
            </span>
          </dd>
        </div>
      </dl>

      <UptimePerClient :uptime="technical.uptimePerClient" />
    </section>

    <section class="marketing">
      <header>
        <h2>{{ $t('marketing') }}</h2>
      </header>

      <dl class="panels">
        <div>
          <dt>{{ $t('campaigns total') }}</dt>
          <dd>
            <span class="value">{{ marketing.campaignStats.total }}</span>
          </dd>
        </div>

        <div>
          <dt>{{ $t('extraordinary campaigns') }}</dt>
          <dd>
            <span class="value">{{ marketing.campaignStats.difficult }}</span>
          </dd>
        </div>

        <div>
          <dt>{{ $t('HDMA pre-influence') }}</dt>
          <dd>
            <span class="value">
              {{ marketing.hdmaActiveRounds }} <small>{{ $t('months') }}</small>
            </span>
          </dd>
        </div>
      </dl>

      <dl class="panels">
        <div v-for="item in marketing.trainingStats" :key="item.type.value">
          <dt>{{ $t('Training :type', { type: item.type.label }) }}</dt>
          <dd>
            <span class="value">
              {{ item.count }}
            </span>
          </dd>
        </div>
      </dl>
    </section>

    <section class="hdma">
      <header>
        <h2>{{ $t('HDMA') }}</h2>
      </header>

      <p v-if="hdmaActive" class="active">{{ $t('HDMA is active') }}</p>
      <p v-else class="inactive">{{ $t('HDMA is not active') }}</p>
    </section>

    <section class="backoffice">
      <header>
        <h2>{{ $t('backoffice') }}</h2>
      </header>

      <dl class="panels">
        <div>
          <dt>{{ $t('offers submitted') }}</dt>
          <dd>
            <span class="value">{{ backoffice.projectsStats.quoted }}</span>
          </dd>
        </div>

        <div>
          <dt>{{ $t('quotes won') }}</dt>
          <dd>
            <span class="value">{{ backoffice.projectsStats.won }}</span>
          </dd>
        </div>
      </dl>
    </section>

    <section class="profit-loss">
      <header>
        <h2>{{ $t('profit & loss') }}</h2>
      </header>

      <dl class="panels">
        <div>
          <dt>{{ $t('revenue') }}</dt>
          <dd>
            <span class="value">
              <MoneyDisplay :value="profitLoss.revenue" />
            </span>
          </dd>
        </div>
        <div>
          <dt>{{ $t('costs') }}</dt>
          <dd>
            <span class="value">
              <MoneyDisplay :value="profitLoss.costs" />
            </span>
          </dd>
        </div>

        <div>
          <dt>{{ $t('profit') }}</dt>
          <dd>
            <strong class="value">
              <MoneyDisplay :value="profitLoss.profit" />
            </strong>
          </dd>
        </div>
      </dl>
    </section>

    <section class="investment-costs">
      <header>
        <h2>{{ $t('investment costs') }}</h2>
      </header>

      <ul>
        <li v-for="item in investmentCosts" :key="item.type.value">
          <span class="type">{{ item.type.label }}</span>
          <span class="value">
            <MoneyDisplay :value="item.value" />
          </span>
        </li>
      </ul>
    </section>
  </div>
</template>

<script setup lang="ts">
import MoneyDisplay from '/@front:components/MoneyDisplay.vue'
import NumberDisplay from '/@front:components/NumberDisplay.vue'
import ClientNps from '/@front:components/results/ClientNps.vue'
import UptimePerClient from '/@front:components/results/UptimePerClient.vue'
import GameLayout from '/@front:layouts/game.vue'
import type { EnumObject, ScenarioClientWithNPS } from '/@front:types/shared'

type InvestmentCost = {
  type: EnumObject
  value: number
}

defineOptions({
  layout: [GameLayout],
})

defineProps<{
  sales: Record<string, any>
  technical: Record<string, any>
  marketing: Record<string, any>
  backoffice: Record<string, any>
  hdmaActive: boolean
  clientsWithNps: ScenarioClientWithNPS[]
  profitLoss: Record<string, number>
  investmentCosts: InvestmentCost[]
}>()
</script>

<style scoped>
section.hdma {
  & p {
    display: flex;
    align-items: center;
    gap: 0.35em;

    &::before {
      --size: 0.8em;

      width: var(--size);
      height: var(--size);
      flex: 0 0 var(--size);
      border-radius: 50%;
      background: var(--green-600);
      content: '';
    }

    &.inactive::before {
      background: var(--red-500);
    }
  }
}
</style>
